import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { clsx } from 'clsx';
import type { ReactNode } from 'react';

interface TrendInfo {
  value: number;
  direction: 'up' | 'down';
}

interface MetricCardProps {
  label: string;
  value: string | number;
  unit?: string;
  subtext?: string;
  trend?: TrendInfo;
  trendInverted?: boolean;
  changePct?: number | null;
  previousValue?: string | number | null;
  icon?: ReactNode;
  className?: string;
}

export default function MetricCard({
  label,
  value,
  unit,
  subtext,
  trend,
  trendInverted = false,
  changePct,
  previousValue,
  icon,
  className,
}: MetricCardProps) {
  let trendColor = 'text-slate-500';
  let TrendIcon: typeof TrendingUp | typeof TrendingDown | typeof Minus = Minus;
  let displayPct: number | null = null;

  if (trend) {
    const isUp = trend.direction === 'up';
    const isGood = trendInverted ? !isUp : isUp;
    trendColor = isGood ? 'text-emerald-600' : 'text-rose-500';
    TrendIcon = isUp ? TrendingUp : TrendingDown;
    displayPct = trend.value;
  } else if (changePct != null) {
    const isUp = changePct > 0;
    trendColor = isUp ? 'text-emerald-600' : 'text-rose-500';
    TrendIcon = isUp ? TrendingUp : changePct < 0 ? TrendingDown : Minus;
    displayPct = Math.abs(changePct);
  }

  return (
    <div
      className={clsx(
        'group rounded-2xl border border-slate-200/60 bg-white p-6 shadow-metric transition-all duration-200 hover:shadow-metric-hover hover:border-slate-200 dark:border-slate-800/80 dark:bg-slate-900 dark:hover:border-slate-700',
        className,
      )}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-[13px] font-medium text-slate-500 dark:text-slate-400">{label}</p>
          <div className="mt-2.5 flex items-baseline gap-1.5">
            <span className="text-metric-value text-slate-900 dark:text-slate-100">{value}</span>
            {unit && <span className="text-sm font-medium text-slate-400 dark:text-slate-500">{unit}</span>}
          </div>
          {subtext && <p className="mt-1.5 text-xs text-slate-400 dark:text-slate-500">{subtext}</p>}
          {(displayPct != null || previousValue != null) && (
            <div className="mt-3 flex items-center gap-1.5">
              <div className={clsx('flex items-center gap-1 rounded-full px-1.5 py-0.5 text-xs font-medium', trendColor, trendColor === 'text-emerald-600' ? 'bg-emerald-50 dark:bg-emerald-950/30' : trendColor === 'text-rose-500' ? 'bg-rose-50 dark:bg-rose-950/30' : 'bg-slate-50 dark:bg-slate-800')}>
                <TrendIcon className="h-3 w-3" />
                {displayPct != null && <span>{displayPct.toFixed(1)}%</span>}
              </div>
              {previousValue != null && (
                <span className="text-xs text-slate-400 dark:text-slate-500">vs {previousValue}</span>
              )}
            </div>
          )}
        </div>
        {icon && (
          <div className="rounded-xl bg-surface-100 p-3 text-slate-500 transition-colors group-hover:bg-brand-50 group-hover:text-brand-600 dark:bg-slate-800 dark:text-slate-400 dark:group-hover:bg-brand-950/40 dark:group-hover:text-brand-400">
            {icon}
          </div>
        )}
      </div>
    </div>
  );
}
